// A named-state ladder whose equilibrium depends on which state an object is
// currently in -- see HeatStateDef's own doc comment (types.ts) for the full
// reasoning against BarrierDef.meltsWhen's single direction-flipping
// threshold. Room9's Bonfire is the first object using this: Unlit decays
// toward bare room ambient, Lit decays toward ambient + its own bias, so it
// genuinely sits hotter than the room rather than merely reaching ambient
// and stopping there.
import { FluidId, HeatStateStage, ObjectDef } from './types';
import { ConceptId } from '../runes/concepts';
import { SimEvents } from './events';
import { PhysicsConfig } from './physics';

/** One state-advance: the first of this stage's `transitions` whose comparison
 * currently holds, or `current` unchanged.
 *
 * **First match wins**, which makes the authored order the priority -- the same
 * rule `vineStage` uses to let Dead override everything however hot the vine is.
 * A transition reads whichever property its own `concept` names, defaulting to
 * the ladder's own, which is what lets one stage be left for two unrelated
 * reasons: a Candle goes out on Energy and melts on Entropy, and putting the
 * melt first is what decides which happens when both are true at once.
 *
 * Pure, and takes a property *reader* rather than a single value, so it stays
 * unit-testable without an object -- the same shape `nextSandStage` already has.
 * An unresolvable `current` (an authoring error, or a stage some future content
 * forgot to define) is a dead end rather than a guess: returns `current` right
 * back.
 *
 * Advances at most one step per call, deliberately, the same restraint
 * `nextSandStage` documents: a candle whose Entropy is drained while it is cold
 * lands on Unlit via one settle and Lit on the next, rather than resolving to a
 * fixpoint inside a single settle. */
export function nextHeatState(
  current: string,
  states: Record<string, HeatStateStage>,
  ownConcept: ConceptId,
  valueOf: (concept: ConceptId) => number,
): string {
  const state = states[current];
  if (!state) return current;
  for (const transition of state.transitions ?? []) {
    const value = valueOf(transition.concept ?? ownConcept);
    if (transition.above !== undefined && value >= transition.above) return transition.to;
    if (transition.below !== undefined && value <= transition.below) return transition.to;
  }
  return current;
}

/** This object's own equilibrium right now: whatever its current state says it
 * holds, or failing that room ambient plus its current state's bias (0 for a
 * plain reservoir state, which is exactly "decays toward ambient like
 * everything else" -- a Source is just a state whose bias moves that target
 * away from ambient). Clamped to `maxProperties`
 * when the object declares one for this concept (the same soft ceiling a
 * regenerating reservoir already respects) -- uncapped otherwise, since
 * nothing in this sim enforces a global ceiling on an arbitrary object's
 * own `properties[concept]`, only player reserves have one. */
export function heatTarget(obj: ObjectDef, ambient: number): number {
  const heatState = obj.heatState!;
  const stage = heatState.states[heatState.current];
  // `holds` is an absolute equilibrium and wins where a stage declares one --
  // see HeatStateStage.holds for why a hearth cannot be expressed as a bias.
  const target = stage?.holds ?? ambient + (stage?.bias ?? 0);
  const max = obj.maxProperties?.[heatState.concept];
  return max !== undefined ? Math.min(target, max) : target;
}

/** Checks `obj.heatState.current` against its live property value and, if
 * `nextHeatState` says it crossed, applies the new state: swaps
 * `obj.texture`/`obj.interact` directly (not just the event -- the same
 * `refreshRustTexture` fix this codebase already made once: nothing else
 * writes `obj.texture` back, so a stage that cycles to its own originally-
 * authored texture would otherwise compare equal to a stale value and skip
 * re-emitting on the next room entry) and pushes `texture-set`/
 * `interact-changed`, mirroring `checkCandleRestore`'s own pattern. Called
 * from inside `checkMeltThreshold` (see that function's own comment on
 * why) rather than as a declared settle system of its own. */
export function checkHeatStateTransition(obj: ObjectDef, out: SimEvents): void {
  const heatState = obj.heatState;
  if (!heatState) return;
  const next = nextHeatState(heatState.current, heatState.states, heatState.concept, (concept) => obj.properties?.[concept] ?? 0);
  if (next === heatState.current) return;
  heatState.current = next;
  const stage = heatState.states[next];
  // A phase change costs something real, and is paid on entry only -- see
  // HeatStateStage.onEnter for why re-paying it every settle would make the
  // stage impossible to leave.
  if (stage?.onEnter) {
    obj.properties = obj.properties ?? {};
    obj.properties[stage.onEnter.concept] = Math.max(obj.properties[stage.onEnter.concept] ?? 0, stage.onEnter.atLeast);
  }
  // A ladder owns its object's shape as well as its look: each stage says
  // whether it can be walked over, and a stage that says nothing means not.
  // Absent-means-solid rather than absent-means-unchanged, because the other
  // reading leaves a restored Candle still walkable -- the melted stage set it
  // and no later stage ever took it back. Same rule as `texture`, where every
  // stage states its own rather than inheriting the last one's.
  obj.walkable = stage?.walkable || undefined;
  if (stage?.texture && obj.texture !== stage.texture) {
    out.push({ kind: 'texture-set', objectId: obj.id, texture: stage.texture, transition: 'cross-fade' });
    obj.texture = stage.texture;
  }
  // A stage that says what it is called says so before it says what it reads
  // as, so the one `interact-changed` below covers both -- that event means
  // "what you are facing reads differently now", and the label is half of that.
  // Unlike the code-owned ladders (see `presentation.ts`), an authored stage may
  // legitimately change one and not the other: a Bonfire catching is the same
  // bonfire, so it restates its description and keeps its name.
  if (stage?.name) obj.name = stage.name;
  if (stage?.name || stage?.interact) {
    if (stage.interact) obj.interact = stage.interact;
    out.push({ kind: 'interact-changed', objectId: obj.id });
  }
  // Said last, so the note describes an object already in its new state.
  if (stage?.narrates) out.push({ kind: 'state-changed', objectId: obj.id, objectName: obj.name, note: stage.narrates });
}

// ---------------------------------------------------------------------------
// Water freeze/thaw: a second caller of nextHeatState above, not a second
// mechanism. Deliberately *not* a HeatStateDef object -- see types.ts's own
// WaterDef.frozen doc comment for why: water tiles never decay toward
// ambient (they never have, and room9's ambient sits below the freeze
// threshold specifically because of the Bonfire's own ignite point -- if
// every channel tile drifted toward ambient like a heatState object does,
// the whole room would eventually freeze on its own), and the threshold
// pair is a universal physical law read from PhysicsConfig, not a per-object
// creative choice authored into 29 near-identical JSON blocks the way a
// Bonfire's own bias legitimately is (rust.ts's own rustEntropy is the same
// "law lives in code, not content" precedent).
// ---------------------------------------------------------------------------

/** What a tile of each fluid *is* once it has set solid.
 *
 * In code and not in the room files, for the same reason the thresholds are:
 * ice is what frozen water is called everywhere, not a creative choice to be
 * made 29 times. The liquid half is authored, because a Riverbed, a Spring's
 * outflow and a standing pool of lava genuinely are different things to look
 * at; the solid half is the fluid itself, set.
 *
 * Only the name and the description live here. The *texture* stays authored
 * (`water.frozenTexture`), because a frozen channel and a frozen pool are drawn
 * differently even though both are ice -- and because `recomputeWaterFlow`, not
 * this module, is the only place that knows which of a tile's three looks
 * applies (see `checkWaterFreeze`'s own note on why it never writes
 * `obj.texture`).
 *
 * Added 2026-08-11, reported as "lava description does not change when frozen":
 * a Basalt Trench cooled until it set still read "The channel again, still
 * running" and was still called a Basalt Trench, with black cooled rock drawn
 * over the top of both. The freeze swapped the one part of the presentation it
 * happened to own and left the other two saying the opposite. */
export const FLUID_SOLID: Record<FluidId, { name: string; description: string }> = {
  water: {
    name: 'Ice',
    description: 'Set solid, edge to edge. Nothing runs through here while it holds -- and ice is a far poorer conductor than the water it was, so nothing crosses it either.',
  },
  lava: {
    name: 'Cooled Basalt',
    description: 'The melt has gone out of it. Black, glassy rock, cool enough to cross and going nowhere -- until something puts the heat back in.',
  },
};

/** The same law for the other direction: what a tile reads as **while fluid is
 * actually running through it**.
 *
 * Added 2026-08-18, reported as "the riverbed descriptions do not update when
 * they have water on them" -- and it was the same bug `FLUID_SOLID` was written
 * for in 2026-08-11, one look further along. A Riverbed the river had been
 * redirected into went on saying *"A dry channel... Nothing moves through it
 * now"* over a sprite full of moving water, which is the world holding two
 * opinions about what you are looking at and printing the wrong one in the
 * larger type.
 *
 * A **law rather than authored content**, for `FLUID_SOLID`'s reason: running
 * water reads the same in any channel, and thirty-two Riverbed tiles do not each
 * want their own sentence about being wet. What is authored is the *dry* look,
 * which is where a channel says what it is and why it is there -- that pair is
 * kept in `water.authored` and handed straight back when the tile dries out.
 *
 * No name, deliberately. `FLUID_SOLID` renames because setting solid genuinely
 * makes it a different substance (ice is not water); a wet riverbed is the same
 * riverbed with water in it. An object for which that is *not* true -- a machine
 * the fluid drives rather than a route it takes -- says so with
 * `water.flowingInteract` instead of being renamed. */
export const FLUID_FLOWING: Record<FluidId, { description: string }> = {
  water: {
    description: 'Water is moving through here, steady and cold, filling the cut from side to side.',
  },
  lava: {
    description: 'Molten rock creeps along the trench, slow and bright, and the air above it shakes with the heat.',
  },
};

/** The two-state (water/ice) states map, built fresh from `physics` on every
 * call rather than stored anywhere -- the thresholds are a physics constant,
 * not authored content, so there is nothing to cache. */
function fluidStates(physics: PhysicsConfig, fluid: FluidId): Record<string, HeatStateStage> {
  const { freezeThreshold, meltThreshold } = physics.fluid[fluid];
  return {
    liquid: { transitions: [{ below: freezeThreshold, to: 'solid' }] },
    solid: { transitions: [{ above: meltThreshold, to: 'liquid' }] },
  };
}

/** Checks a water channel tile's own Energy against the freeze/melt pair and
 * flips `obj.water.frozen` if it crossed -- the same "real, persisted state,
 * checked through nextHeatState" shape `checkHeatStateTransition` above
 * uses, just sourced from `PhysicsConfig.water` instead of `obj.heatState`.
 * A no-op for anything that isn't an ordinary channel tile (no
 * `water.flowingTexture` -- the source, a bridge, and a reservoir terminus
 * are governed by their own, different mechanisms already).
 *
 * `hasWater` (default `true`, so the pure threshold behavior below is
 * unaffected when a caller doesn't pass one) gates the water->ice direction
 * only: a tile the water-flow trace doesn't currently reach has nothing on
 * it to freeze, no matter how cold a cast pushes its Energy. Never gates
 * ice->water -- a frozen tile is, by construction, never part of the
 * flowing set while it's frozen (`isWaterPassable` excludes it), so
 * requiring `hasWater` to thaw would make an already-frozen tile unmeltable.
 * The real reachability set lives on `WorldState.flowingTiles`, computed by
 * `recomputeWaterFlow`/`traceWaterFlow` from the *previous* settle -- exactly
 * "was there water here a moment ago," which is what a freezing cast should
 * be judged against.
 *
 * Never touches `obj.texture` -- both directions are left entirely to
 * `recomputeWaterFlow`'s own next pass (always run immediately after, via
 * `settleWorld`, by every real caller), which is the only place that knows
 * whether a liquid tile is actually reached by flow (`flowingTexture`) or
 * merely thawed and dry (the base texture). Mutating `obj.texture` here used
 * to overwrite that base texture permanently on freezing, so a tile that
 * later thawed while outside the flowing set had no correct texture left to
 * fall back to -- it stayed visually frozen forever even after
 * `water.frozen` correctly flipped back to `false`.
 *
 * It does not swap the name or the Inspect text either, since 2026-08-18 -- it
 * used to (2026-08-11, reported as "lava description does not change when
 * frozen"), and that was one place deciding two thirds of a presentation from a
 * flag while another place decided the last third from the flow. Once the
 * *flowing* look grew a name and a description too, that would have been three
 * writers and two separate memories of the authored pair. `recomputeWaterFlow`
 * owns all four looks now, from one branch, reading `FLUID_SOLID` below. This
 * still takes a `SimEvents` sink, purely to say `interact-changed` -- the flag
 * it just flipped is what the pass will read. */
export function checkWaterFreeze(obj: ObjectDef, physics: PhysicsConfig, out: SimEvents, hasWater = true): void {
  // Any tile that can hold fluid -- a channel (`flowingTexture`) or a standing
  // body (`body`). Not just channels, since 2026-08-09: a lava pool is lava,
  // and cooling it should set it into rock exactly as cooling a channel does.
  //
  // A **bridge** is excluded explicitly (2026-08-11). It is a structure that
  // carries a fluid across a gap, not a body of one: what decides whether it is
  // passable is its own repair state (see `WaterDef.bridge`), and it has no
  // fluid of its own to set. Stated here rather than falling out of "it has no
  // flowingTexture", which is what used to keep it out -- room9's Aqueduct now
  // has one, so that accident no longer holds and the invariant needs saying.
  if (!obj.water || obj.water.bridge || (!obj.water.flowingTexture && !obj.water.body)) return;
  const energy = obj.properties?.energy ?? 0;
  const current = obj.water.frozen ? 'solid' : 'liquid';
  const next = nextHeatState(current, fluidStates(physics, obj.water.fluid ?? 'water'), 'energy', () => energy);
  if (next === current) return;
  if (next === 'solid' && !hasWater) return;
  obj.water.frozen = next === 'solid';

  // The *look* is not this function's to write, and stopped being so on
  // 2026-08-18. It used to swap the name and the Inspect text here and leave the
  // texture to `recomputeWaterFlow`, which was already two places deciding one
  // object's presentation from two readings of the same state. Once the flowing
  // look grew a name and a description of its own there would have been three,
  // with two separate memories of the authored pair to keep from clobbering each
  // other. So all four looks -- dry, flowing, frozen, dammed -- are now decided
  // by one branch in that pass, which is the only place that can see the flow as
  // well as the flag. This writes the flag and says it changed.
  out.push({ kind: 'interact-changed', objectId: obj.id });
}
